/**
 * A card: the white, hairline-bordered, rounded surface almost everything in this app sits on.
 *
 * Webwin has no `Border` control, and `Panel`'s own styles are close but not this: `raised` paints
 * the theme's `drawPanel`, which uses the 4px control radius and the control border, and then - when
 * a background is given - repaints an inner rectangle deflated by 2px, which squares off the corner
 * it just rounded. So a card is twenty lines of `onPaint` rather than a configuration of Panel.
 *
 * Subclassing `Panel` and not `Control` is what matters: Panel already computes `clientRect` inside
 * the padding and implements `preferredSize` as content-plus-frame, so a card inside an auto-sized
 * stack grows with its own contents, which is the whole reason the page-level layout works.
 *
 * TINT carries the CRM's status surfaces - the amber banner, the red warning, the green chip - so a
 * status panel is a card with a role rather than another control.
 */
import {
  insets,
  Panel,
  type Color,
  type Control,
  type PanelOptions,
  type Point,
  type RenderContext,
  type Size,
  type Theme
} from 'webwin';
import { crm } from '../theme/CrmTheme.js';

export type CardTint = 'card' | 'alt' | 'danger' | 'warn' | 'ok' | 'info' | 'brand' | 'sidebar';

export interface CardOptions extends PanelOptions {
  tint?: CardTint;
  /** Corner radius. 8 for a card, 3 for a chip - the two the design actually uses. */
  radius?: number;
  /** A hairline border. On for a card; off for a tinted banner, which reads as a fill. */
  bordered?: boolean;
}

export class Card extends Panel {
  tint: CardTint;
  radius: number;
  bordered: boolean;

  constructor(options: CardOptions = {}) {
    super({ padding: insets(16), ...options });
    this.tint = options.tint ?? 'card';
    this.radius = options.radius ?? 8;
    this.bordered = options.bordered ?? true;
  }

  /**
   * Makes the whole card one click target.
   *
   * WEBWIN DOES NOT BUBBLE A CLICK, and a `Panel` takes the hit itself unless it is `clickThrough` -
   * so a card whose row is a stack of stacks only ever saw a click that landed on its 16px padding,
   * and `card.on('click')` looked wired and did nothing. In clickable mode a hit inside the card goes
   * to the card unless it landed on something that takes input of its own (anything with a tab stop,
   * or inside one - a button, a link, a box, a combo), so a row can still carry its own buttons.
   */
  onActivate(handler: () => void): this {
    this.#clickable = true;
    this.cursor = 'pointer';
    this.on('click', () => handler());
    return this;
  }

  #clickable = false;

  override hitTest(p: Point): Control | null {
    const hit = super.hitTest(p);
    if (!this.#clickable || hit === null || hit === this) return hit;
    for (let c: Control | null = hit; c && c !== this; c = c.parent) {
      if (c.tabStop) return hit;
    }
    return this;
  }

  /**
   * Measures a stretched, self-measuring child at the card's own width.
   *
   * Webwin's container measure counts a child that is not docked by the size it currently HAS -
   * right for something placed by coordinates, wrong for the row a card nearly always holds, whose
   * height depends on the width the card is about to give it. Read as-is, a card was sized from its
   * row's height at the last width it had, and wrapped text at the new width hung off the bottom.
   */
  protected override measureContent(available: Size): Size {
    const base = super.measureContent(available);
    const children = this.controls.toArray().filter(c => c.visible);
    // Docked children are measured properly by the base; only the coordinate-placed case is fixed.
    if (children.length === 0 || children.some(c => c.dock !== 'none')) return base;
    // The preferred size REPLACES the current one rather than being max'd with it: a size taken from
    // an early pass is often too big, and a max would keep it for good. That holds for the width as
    // much as the height - a card measured from its row's current width, when that row was sized
    // from the card, reported its own stale width back and never fitted a narrower column.
    // A card with a width of its own (centred, not stretched or measured) is laid out at that width,
    // so it is measured at it too. The parent's alignment pass offers the whole client width, and a
    // fixed-width sign-in card measured at 800px wrapped its text onto fewer lines than it really
    // gets, came out short, and clipped its last button.
    const ownWidth = !this.autoWidth && this.hAlign !== 'stretch' && this.width > 0;
    const limit = ownWidth ? Math.min(available.width, this.clientSize.width) : available.width;
    let width = 0;
    let height = 0;
    for (const child of children) {
      const m = child.margin;
      const stretched = child.hAlign === 'stretch';
      // A child that sizes its own width is offered the room, not the width it already has: offered
      // its own (stale) width it reports that back, and a chip kept a width 2px short of its label.
      const room = Math.max(0, limit - m.left - m.right);
      const offerWidth = stretched || child.autoWidth ? room : child.width;
      let w = child.width;
      let h = child.height;
      if (child.autoHeight || child.autoWidth || stretched) {
        const pref = child.preferredSize({ width: offerWidth, height: available.height });
        if (child.autoHeight) h = pref.height;
        if (child.autoWidth || stretched) w = Math.min(pref.width, offerWidth || pref.width);
      }
      width = Math.max(width, child.x + w + m.right);
      height = Math.max(height, child.y + h + m.bottom);
    }
    return { width, height };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    let fill: Color;
    let border: Color;
    switch (this.tint) {
      case 'alt':
        fill = c.altSurface;
        border = c.border;
        break;
      case 'danger':
        fill = c.dangerBg;
        border = c.dangerBorder;
        break;
      case 'warn':
        fill = c.warnBg;
        border = c.warnBorder;
        break;
      case 'ok':
        fill = c.okBg;
        border = c.ok;
        break;
      case 'info':
        fill = c.infoBg;
        border = c.info;
        break;
      case 'brand':
        fill = c.brand;
        border = c.brandDark;
        break;
      case 'sidebar':
        fill = c.sidebar;
        border = c.sidebar;
        break;
      default:
        fill = c.card;
        border = c.border;
        break;
    }

    rc.drawRect(this.localRect, {
      fill,
      radius: this.radius,
      ...(this.bordered ? { borderWidth: 1, borderColor: border } : {})
    });
  }
}
